import { Component, OnInit } from '@angular/core';
import { Label, LabelColor, UserLabel } from '@app/pcs/models';
import * as fromPcs from '@app/pcs/reducer';
import {
  MultiSelectGridFilterDirective
} from '@app/pcs/shared/containers/search-results-grid/filters/multi-select-grid-filter.directive';
import { getColorForLabel, getLabelColor } from '@app/pcs/util/user-label';
import { select, Store } from '@ngrx/store';
import { getId, hasNoValue, hasValue, isArray, isBoolean, isNotEmpty, isTrue } from '@util';
import { FilteringOptions } from 'ngrx-data-grid';
import { Color } from 'pcs-component-library';
import * as R from 'ramda';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

type ToLabels = (userLabels: UserLabel[]) => Label[];
const toLabels: ToLabels = R.ifElse(hasValue, R.map(R.prop('label')), R.always(null));

type ToColors = (userLabels: UserLabel[]) => Color[];
const toColors: ToColors = R.ifElse(hasValue, R.map(R.compose(getColorForLabel, getLabelColor)), R.always(null));

const isInstanceOfColor = (value: any) => R.has('id', value) && R.has('value', value);

const toOptionIds = R.cond([
  [hasNoValue, R.always([])],
  [isArray, value => R.map(getId, value)],
  [R.T, value => [getId(value)]]
]);

const contains = (optionIds: any[]) => R.compose(isNotEmpty, R.intersection(optionIds));
const notContains = (optionIds: any[]) => R.compose(R.isEmpty, R.intersection(optionIds));

const filterByOption = (option: FilteringOptions, optionIds: number[]) => R.cond([
  [
    R.equals(FilteringOptions.Contains),
    R.always(contains(optionIds))
  ],
  [
    R.equals(FilteringOptions.NotContains),
    R.always(notContains(optionIds))
  ]
])(option);


@Component({
  templateUrl: './labels-filter.component.html'
})
export class LabelsFilterComponent extends MultiSelectGridFilterDirective<Label | Color> implements OnInit {
  options$: Observable<Label[]> = this.store.pipe(select(fromPcs.getLabels), map(toLabels));
  readonly colorOptions = R.map(getColorForLabel, R.values(LabelColor));
  selectedColorOptions: Color[];

  filterByColor = false;

  constructor(store: Store<any>) {
    super(store);
  }

  ngOnInit(): void {
    if (isBoolean(this.value)) {
      this.showOnlyRowsWithNoOptions = this.value;
    } else if (isArray(this.value)) {
      this.filterByColor = hasValue(this.value) && isInstanceOfColor(R.head(this.value));
      if (this.filterByColor) {
        this.selectedColorOptions = this.value as Color[];
      } else {
        this.selectedOptions = this.value;
      }
    }
  }

  filter({option, value, rawValue}) {
    if (isTrue(value)) {
      return hasNoValue(rawValue);
    } else if (hasValue(value) && isInstanceOfColor(R.head(value))) {
      return contains(toOptionIds(value))(toOptionIds(toColors(rawValue))) as boolean;
    } else {
      return filterByOption(option, toOptionIds(value))(toOptionIds(toLabels(rawValue))) as boolean;
    }
  }

  onFilterByColorChanged(value) {
    this.filterByColor = value;
  }

  onOptionSelected(options: Label[]) {
    this.selectedOptions = options;
    this.valueChanged.emit(this.selectedOptions);
  }

  onColorOptionSelected(options: Color[]) {
    this.selectedColorOptions = options;
    this.valueChanged.emit(this.selectedColorOptions);
  }
}
